import { API_BASE_URL } from "@/shared/config/apiBaseUrl";

export type AuthSession = {
  user: {
    user_id: string;
    username: string;
    primary_email: string | null;
    email_verified: boolean;
    avatar_url: string | null;
    created_at: string;
  };
  session_token: string;
};

export type SignupPayload = {
  email: string;
  username: string;
  password: string;
  confirm_password: string;
  turnstile_token?: string | null;
};

export type LoginPayload = {
  email: string;
  password: string;
};

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

export type TurnstileConfig = {
  enabled: boolean;
  site_key: string | null;
};

async function parseJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let detail = `request failed with status ${response.status}`;
    try {
      const payload = (await response.json()) as { detail?: string };
      if (payload.detail) {
        detail = payload.detail;
      }
    } catch {
      // Keep fallback message when response body is not JSON.
    }
    throw new ApiError(detail, response.status);
  }

  return (await response.json()) as T;
}

export async function signup(payload: SignupPayload): Promise<AuthSession> {
  const response = await fetch(`${API_BASE_URL}/api/auth/signup`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  return parseJson<AuthSession>(response);
}

export async function getTurnstileConfig(): Promise<TurnstileConfig> {
  const response = await fetch(`${API_BASE_URL}/api/auth/turnstile/config`);
  return parseJson<TurnstileConfig>(response);
}

export async function login(payload: LoginPayload): Promise<AuthSession> {
  const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  return parseJson<AuthSession>(response);
}

export async function getCurrentUser(sessionToken: string): Promise<AuthSession["user"]> {
  const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
    headers: {
      Authorization: `Bearer ${sessionToken}`,
    },
  });

  return parseJson<AuthSession["user"]>(response);
}

export function beginGoogleLogin() {
  window.location.assign(`${API_BASE_URL}/api/auth/google/start`);
}

export function beginLichessLogin() {
  window.location.assign(`${API_BASE_URL}/api/auth/lichess/start`);
}
